import { getApiBaseUrl } from "../config";

// Kept in localStorage so every tab shares one session clock: a refresh or sign-out in one
// tab is seen by the others.
export const SESSION_EXPIRES_AT_KEY = "kepro.session.expiresAt";
const ABSOLUTE_EXPIRES_AT_KEY = "kepro.session.absoluteExpiresAt";
const IDLE_TIMEOUT_KEY = "kepro.session.idleTimeoutMs";
const LAST_REFRESH_AT_KEY = "kepro.session.lastRefreshAt";

const DEFAULT_IDLE_TIMEOUT_MS = 15 * 60 * 1000;

// Activity refreshes the session at most this often, so the server's expiry never trails
// the user's last activity by more than this.
export const REFRESH_THROTTLE_MS = 60 * 1000;

const readNumber = (key) => {
  try {
    const value = Number(localStorage.getItem(key));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
};

const write = (key, value) => {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // Storage can be unavailable (private mode, blocked site data). The server still
    // enforces the timeout; the browser just can't warn ahead of it.
  }
};

export const getSessionExpiresAt = () => readNumber(SESSION_EXPIRES_AT_KEY);
export const getAbsoluteExpiresAt = () => readNumber(ABSOLUTE_EXPIRES_AT_KEY);
export const getLastRefreshAt = () => readNumber(LAST_REFRESH_AT_KEY);
export const getIdleTimeoutMs = () =>
  readNumber(IDLE_TIMEOUT_KEY) ?? DEFAULT_IDLE_TIMEOUT_MS;

// The server sends durations rather than timestamps, so a wrong client clock can't shift
// when the session appears to end.
export const recordSession = (session) => {
  if (!session?.expiresInMs) return;
  const now = Date.now();
  write(SESSION_EXPIRES_AT_KEY, now + session.expiresInMs);
  write(LAST_REFRESH_AT_KEY, now);
  if (session.idleTimeoutMs) write(IDLE_TIMEOUT_KEY, session.idleTimeoutMs);
  if (session.absoluteExpiresInMs) {
    write(ABSOLUTE_EXPIRES_AT_KEY, now + session.absoluteExpiresInMs);
  }
};

// The idle timeout is kept so the sign-in page can say how long the timeout was.
export const clearSessionClock = () => {
  try {
    [SESSION_EXPIRES_AT_KEY, ABSOLUTE_EXPIRES_AT_KEY, LAST_REFRESH_AT_KEY].forEach((key) =>
      localStorage.removeItem(key),
    );
  } catch {
    // See write().
  }
};

const requestRefresh = async () => {
  try {
    const response = await fetch(`${getApiBaseUrl()}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (response.status === 401) return "expired";
    if (!response.ok) return "failed";

    const payload = await response.json();
    recordSession(payload?.data?.session);
    return "refreshed";
  } catch {
    return "failed";
  }
};

let inFlight = null;

// Resolves to "refreshed", "expired" (the server says the session is over) or "failed"
// (network or server error, worth retrying on the next activity). Only one tab refreshes
// at a time: the refresh token is single-use, and the cookies it sets are shared by every
// tab anyway.
export const refreshSession = ({ force = false } = {}) => {
  if (inFlight) return inFlight;

  const run = async () => {
    // Another tab may have refreshed while this one waited for the lock.
    const lastRefreshAt = getLastRefreshAt();
    if (!force && lastRefreshAt && Date.now() - lastRefreshAt < REFRESH_THROTTLE_MS) {
      return "refreshed";
    }
    return requestRefresh();
  };

  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  inFlight = (locks ? locks.request("kepro-session-refresh", run) : run())
    .catch(() => "failed")
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
};
